import type {
  AdjustmentBody,
  Application,
  ApplicationStatus,
  CompensationBody,
  ConfigVersion,
  DealCompensation,
  LedgerEntry,
  MarkReceivedResponse,
  Partner,
  PartnerDetail,
  PartnerListItem,
  PartnerWriteBody,
  PayoutRequest,
  PayoutStatus,
  ProgramConfig,
  Referral,
  ReviewBody,
  ReviewResponse,
  VolumeSource,
  VolumeSourceBody,
} from 'views/Partners/types'

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2/admin`

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(`${status}: ${message}`)
    this.status = status
  }
}

const request = async <T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
): Promise<T> => {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers:
      body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const text = await res.text()
  let parsed: any = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parsed = null
  }
  if (!res.ok) {
    const message = parsed?.error || parsed?.info || text || res.statusText
    throw new ApiError(res.status, message)
  }
  return (
    parsed && typeof parsed === 'object' && 'data' in parsed
      ? parsed.data
      : parsed
  ) as T
}

export const listPartners = () => request<PartnerListItem[]>('GET', '/partners')

export const createPartner = (body: PartnerWriteBody) =>
  request<Partner>('POST', '/partners', body)

export const getPartner = (id: number) =>
  request<PartnerDetail>('GET', `/partners/${id}`)

export const updatePartner = (id: number, body: PartnerWriteBody) =>
  request<Partner>('PUT', `/partners/${id}`, body)

export const listPartnerReferrals = (id: number) =>
  request<Referral[]>('GET', `/partners/${id}/referrals`)

export const createAdjustment = (id: number, body: AdjustmentBody) =>
  request<LedgerEntry>('POST', `/partners/${id}/adjustments`, body)

export const listApplications = (status: ApplicationStatus) =>
  request<Application[]>('GET', `/partner-applications?status=${status}`)

export const reviewApplication = (id: number, body: ReviewBody) =>
  request<ReviewResponse>('POST', `/partner-applications/${id}/review`, body)

export const listPayouts = (status: PayoutStatus) =>
  request<PayoutRequest[]>('GET', `/partner-payouts?status=${status}`)

export const fulfilPayout = (id: number, txHash: string) =>
  request<PayoutRequest>('POST', `/partner-payouts/${id}/fulfil`, {
    tx_hash: txHash,
  })

export const cancelPayout = (id: number) =>
  request<PayoutRequest>('POST', `/partner-payouts/${id}/cancel`)

export const getProgramConfig = () =>
  request<ConfigVersion>('GET', '/partner-program/config')

export const createProgramConfig = (cfg: ProgramConfig) =>
  request<ConfigVersion>('POST', '/partner-program/config', cfg)

export const getVolumeSource = (dealId: string) =>
  request<VolumeSource>('GET', `/deals/${dealId}/volume-source`)

export const setVolumeSource = (dealId: string, body: VolumeSourceBody) =>
  request<VolumeSource>('PUT', `/deals/${dealId}/volume-source`, body)

export const getCompensation = (dealId: string) =>
  request<DealCompensation | null>('GET', `/deals/${dealId}/compensation`)

export const setCompensation = (dealId: string, body: CompensationBody) =>
  request<DealCompensation>('PUT', `/deals/${dealId}/compensation`, body)

export const markCompensationReceived = (dealId: string) =>
  request<MarkReceivedResponse>(
    'POST',
    `/deals/${dealId}/compensation/received`,
  )
